import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FaPlus, FaTrash } from "react-icons/fa";
import {
  createPurchaseRequest, updatePurchaseRequest, getPurchaseRequest,
  searchPurchaseRequestUsers, getPurchaseRequestSiteOptions,
} from "redux/erp/purchaseRequestSlice";
import SlideDrawer from "../purchase/common/SlideDrawer";
import { FieldLabel } from "../purchase/common/PageShell";
import FormSection from "../purchase/common/FormSection";
import Clickable from "../purchase/common/Clickable";
import UserPicker from "../purchase/common/UserPicker";
import SiteSelect from "../purchase/common/SiteSelect";
import { GeistInput } from "../purchase/common/GeistField";
import StepperBar from "../purchase/common/Stepper";

// flow: chuỗi ký digital của từng mẫu — QF-LGS-01 (raw) KHÔNG qua Financial Controller
// (mẫu giấy chỉ có 3 chữ ký), QF-PUR-01 có thêm bước Budget Verification (FC).
const FORM_TYPES = [
  { value: "raw_material", label: "Raw Material / Finished Goods", iso: "QF-LGS-01",
    flow: "Prepared by → Head of Dept → General Manager" },
  { value: "non_raw_material_service", label: "Assets / Non-Raw Items / Services", iso: "QF-PUR-01",
    flow: "Prepared by → Head of Dept → Financial Controller → General Manager" },
];
const FORM_ISO = (t) => (FORM_TYPES.find((x) => x.value === t) || FORM_TYPES[0]).iso;

const STEPS = [
  { key: "basic", label: "Basic info" },
  { key: "vendor", label: "Vendor & delivery" },
  { key: "items", label: "Items & notes" },
];

// 3 nhóm checkbox trên mẫu giấy QF-LGS-01 (raw material) — khớp Crm::PurchaseRequest
// REASON_KINDS / DELIVERY_KINDS / ATTACHMENT_KINDS phía API. "Other" đi kèm text tự do
// (reason_for_purchase / delivery_to sẵn có).
const REASON_KINDS = ["Minimum Inventory Stock", "New Big Job", "Non-Standard Items", "Change Order", "Site Claim", "Other"];
const DELIVERY_KINDS = ["RFF", "MFF", "Job Site", "Other"];
const ATTACHMENT_KINDS = ["BOM", "Inventory Report", "CO", "Site Claim", "Other"];

const CheckPill = ({ checked, label, onClick }) => (
  <Clickable onClick={onClick}
    className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-1.5 tw-rounded-geist-sm tw-text-xs tw-font-medium tw-border tw-border-solid ${
      checked ? "tw-bg-geist-accent tw-text-white tw-border-geist-accent" : "tw-bg-white tw-text-geist-fg-2 tw-border-geist-border hover:tw-bg-geist-bg-2"
    }`}>
    <i className={`bi ${checked ? "bi-check-square-fill" : "bi-square"}`} /> {label}
  </Clickable>
);

CheckPill.propTypes = {
  checked: PropTypes.bool,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
};

const TH = "tw-px-2 tw-py-2 tw-text-left tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-geist-fg-3 tw-whitespace-nowrap";
const cellInput = "tw-w-full tw-px-2 tw-py-1.5 tw-text-[13px] tw-bg-transparent tw-border-0 tw-border-b tw-border-solid tw-border-geist-border-2 hover:tw-border-geist-fg-3 focus:tw-border-geist-accent focus:tw-outline-none tw-transition-colors";

const emptyItem = () => ({
  _key: Math.random().toString(36).slice(2),
  code_no: "", description: "", required_date: "", unit: "", qty: "", unit_price: "", amount: "", remark: "",
});

// Tự fill từ hồ sơ user đang đăng nhập (profile_user) — đỡ gõ lại thông tin đã có sẵn:
// Site (profile.site.id), Department (profile.employee.department), Requester position
// (profile.employee.title), và gợi ý Head of Dept = quản lý trực tiếp (employee.ancestors,
// phần tử CUỐI = quản lý gần nhất theo cây tổ chức) — user vẫn đổi được nếu không đúng.
const blankForm = (formType, profile = {}) => {
  const emp = profile.employee || {};
  const directManager = (emp.ancestors || []).slice(-1)[0];
  return {
    form_type: formType || "raw_material",
    department: emp.department || "",
    requester_position: emp.title || "",
    from_dept: formType === "raw_material" ? "Warehouse" : "",
    to_dept: "Purchasing Department",
    request_date: new Date().toISOString().slice(0, 10),
    site_id: profile.site?.id || "",
    vendor_name: "", vendor_address: "", vendor_phone: "", vendor_fax: "", vendor_attn: "", vendor_title: "",
    reason_for_purchase: "",
    reason_kinds: [], delivery_kind: "", attachment_kinds: [],
    delivery_to: "", delivery_location: "", delivery_attn: "",
    // directManager là Organization::Employee → phải dùng user_id (User id thật),
    // KHÔNG dùng .id (employee id) làm head_of_dept_user_id. Chỉ gợi ý sẵn khi
    // quản lý có tài khoản User; nếu không, để trống cho user tự chọn.
    head_of_dept: directManager?.user_id
      ? { id: directManager.user_id, email: directManager.email, name: directManager.full_name }
      : null,
    notes: "",
    items: [emptyItem()],
  };
};

const PurchaseRequestDrawer = ({ show, onHide, purchaseRequest, initialFormType, onSaved }) => {
  const dispatch = useDispatch();
  const profile = useSelector((s) => s.profile_user?.data || {});
  const [form, setForm] = useState(blankForm(initialFormType, profile));
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const isEdit = !!purchaseRequest?.id;
  const nonRaw = form.form_type === "non_raw_material_service";

  useEffect(() => {
    if (!show) return;
    setStep(0);
    if (purchaseRequest?.id) {
      setForm({
        form_type: purchaseRequest.form_type || "raw_material",
        department: purchaseRequest.department || "",
        requester_position: purchaseRequest.requester_position || "",
        from_dept: purchaseRequest.from_dept || "",
        to_dept: purchaseRequest.to_dept || "Purchasing Department",
        request_date: purchaseRequest.request_date || new Date().toISOString().slice(0, 10),
        site_id: purchaseRequest.site_id || "",
        vendor_name: purchaseRequest.vendor_name || "",
        vendor_address: purchaseRequest.vendor_address || "",
        vendor_phone: purchaseRequest.vendor_phone || "",
        vendor_fax: purchaseRequest.vendor_fax || "",
        vendor_attn: purchaseRequest.vendor_attn || "",
        vendor_title: purchaseRequest.vendor_title || "",
        reason_for_purchase: purchaseRequest.reason_for_purchase || "",
        reason_kinds: purchaseRequest.reason_kinds || [],
        delivery_kind: purchaseRequest.delivery_kind || "",
        attachment_kinds: purchaseRequest.attachment_kinds || [],
        delivery_to: purchaseRequest.delivery_to || "",
        delivery_location: purchaseRequest.delivery_location || "",
        delivery_attn: purchaseRequest.delivery_attn || "",
        head_of_dept: purchaseRequest.head_of_dept_email
          ? { id: purchaseRequest.head_of_dept_user_id, email: purchaseRequest.head_of_dept_email, name: purchaseRequest.head_of_dept_name }
          : null,
        notes: purchaseRequest.notes || "",
        items: (purchaseRequest.items || []).map((i) => ({ _key: `db-${i.id}`, ...i })).length
          ? (purchaseRequest.items || []).map((i) => ({ _key: `db-${i.id}`, ...i }))
          : [emptyItem()],
      });
    } else {
      setForm(blankForm(initialFormType, profile));
    }
    // Seed only when the drawer opens or its source record changes: `profile` is a fresh
    // `{}` fallback on every render and would wipe what the user is typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, purchaseRequest, initialFormType]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setItem = (key, patch) => setForm((f) => ({
    ...f,
    items: f.items.map((it) => {
      if (it._key !== key) return it;
      const next = { ...it, ...patch };
      if (nonRaw) next.amount = (Number(next.qty) || 0) * (Number(next.unit_price) || 0);
      return next;
    }),
  }));

  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, emptyItem()] }));
  const removeItem = (key) => setForm((f) => ({
    ...f,
    items: f.items.length > 1 ? f.items.filter((it) => it._key !== key) : f.items,
  }));

  const totalWeight = form.items.reduce((s, i) => s + (Number(i.qty) || 0), 0);
  const totalAmount = form.items.reduce((s, i) => s + (Number(i.amount) || 0), 0);

  // Validate từng bước trước khi cho "Continue" — tránh để tới cuối mới báo thiếu field.
  const validateStep = (s) => {
    if (s === 0) {
      if (!form.department.trim()) { toast.warning("Please enter Department."); return false; }
      if (!form.site_id) { toast.warning("Please select a Site."); return false; }
      if (!form.head_of_dept?.id) { toast.warning("Please select the Head of Dept (approver)."); return false; }
    }
    return true;
  };
  const goNext = () => { if (validateStep(step)) setStep((s) => Math.min(s + 1, STEPS.length - 1)); };
  const goBack = () => setStep((s) => Math.max(s - 1, 0));
  const jumpTo = (idx) => { if (idx <= step || validateStep(step)) setStep(idx); };

  const onSubmit = async () => {
    if (!validateStep(0)) { setStep(0); return; }
    if (!form.items.some((i) => i.description?.trim())) { toast.warning("Please add at least 1 item line."); return; }

    setBusy(true);
    try {
      const data = {
        form_type: form.form_type,
        department: form.department,
        requester_position: form.requester_position,
        from_dept: form.from_dept,
        to_dept: form.to_dept,
        request_date: form.request_date,
        site_id: form.site_id,
        vendor_name: form.vendor_name,
        vendor_address: form.vendor_address,
        vendor_phone: form.vendor_phone,
        vendor_fax: form.vendor_fax,
        vendor_attn: form.vendor_attn,
        vendor_title: form.vendor_title,
        reason_for_purchase: form.reason_for_purchase,
        reason_kinds: form.reason_kinds,
        delivery_kind: form.delivery_kind,
        attachment_kinds: form.attachment_kinds,
        delivery_to: form.delivery_to,
        delivery_location: form.delivery_location,
        delivery_attn: form.delivery_attn,
        head_of_dept_user_id: form.head_of_dept?.id || "",
        notes: form.notes,
        items_attributes: form.items
          .filter((i) => i.description?.trim())
          .map((i) => ({
            id: typeof i.id === "number" ? i.id : undefined,
            code_no: i.code_no, description: i.description, required_date: i.required_date || null,
            unit: i.unit, qty: i.qty || 0, unit_price: i.unit_price || 0, amount: i.amount || 0, remark: i.remark,
          })),
      };

      let newId;
      if (isEdit) {
        await dispatch(updatePurchaseRequest({ id: purchaseRequest.id, data })).unwrap();
        toast.success("Purchase request updated.");
        dispatch(getPurchaseRequest(purchaseRequest.id));
      } else {
        const res = await dispatch(createPurchaseRequest(data)).unwrap();
        newId = res?.data?.id;
        toast.success("Purchase request created.");
      }
      onSaved?.(newId);
      onHide();
    } catch (e) {
      toast.error(e?.error || e?.message || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  const title = (
    <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
      <span className="tw-inline-flex tw-h-8 tw-w-8 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-geist-sm tw-bg-geist-100 tw-text-geist-accent">
        <i className="bi bi-cart-plus" />
      </span>
      <h2 className="tw-text-[15px] tw-font-semibold tw-text-geist-fg tw-m-0 tw-truncate">
        {isEdit ? `Edit ${purchaseRequest?.code}` : "New Purchase Requisition"}
      </h2>
      <span className="tw-text-xs tw-font-normal tw-text-geist-fg-3 tw-flex-shrink-0">· {FORM_ISO(form.form_type)}</span>
    </div>
  );

  const isLastStep = step === STEPS.length - 1;
  const footer = (
    <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
      {step > 0 ? (
        <Clickable onClick={goBack} disabled={busy}
          className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-9 tw-px-3 tw-rounded-geist-sm tw-text-geist-fg-2 tw-text-[13px] tw-font-medium hover:tw-bg-geist-100">
          <i className="bi bi-arrow-left" /> Back
        </Clickable>
      ) : <span />}
      <div className="tw-flex tw-items-center tw-gap-2">
        <Clickable onClick={onHide} disabled={busy}
          className="tw-inline-flex tw-items-center tw-h-9 tw-px-4 tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border tw-text-geist-fg-2 tw-text-[13px] tw-font-medium hover:tw-bg-geist-100">
          Cancel
        </Clickable>
        {isLastStep ? (
          <Clickable onClick={onSubmit} disabled={busy}
            className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-9 tw-px-4 tw-rounded-geist-sm tw-bg-geist-accent tw-text-white tw-text-[13px] tw-font-medium hover:tw-bg-geist-accent-2 disabled:tw-opacity-60">
            {busy && <i className="bi bi-arrow-repeat tw-animate-spin" />}
            {busy ? "Saving..." : "Save"}
          </Clickable>
        ) : (
          <Clickable onClick={goNext} disabled={busy}
            className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-9 tw-px-4 tw-rounded-geist-sm tw-bg-geist-accent tw-text-white tw-text-[13px] tw-font-medium hover:tw-bg-geist-accent-2">
            Continue <i className="bi bi-arrow-right" />
          </Clickable>
        )}
      </div>
    </div>
  );

  return (
    <SlideDrawer show={show} onHide={onHide} title={title} footer={footer} width="72%" minWidth="820px" bodyClassName="tw-bg-geist-bg-2">
      <div className="geist-scope tw-space-y-4">
        {/* Form type — segmented pill, luôn hiện bất kể đang ở bước nào. */}
        <div className="tw-flex tw-items-center tw-gap-2 tw-bg-geist-bg tw-rounded-geist-md tw-border tw-border-solid tw-border-geist-border tw-shadow-geist-card tw-px-4 tw-py-3 tw-flex-wrap">
          <span className="tw-text-xs tw-font-bold tw-uppercase tw-tracking-wider tw-text-geist-fg-3 tw-mr-1">Form type</span>
          {FORM_TYPES.map((ft) => (
            <Clickable key={ft.value} stopPropagation
              onClick={() => !isEdit && setField("form_type", ft.value)}
              className={`tw-px-3 tw-py-1.5 tw-rounded-geist-sm tw-text-xs tw-font-semibold tw-border tw-border-solid ${
                form.form_type === ft.value
                  ? "tw-bg-geist-accent tw-text-white tw-border-geist-accent"
                  : "tw-bg-white tw-text-geist-fg-2 tw-border-geist-border hover:tw-bg-geist-bg-2"
              } ${isEdit ? "tw-opacity-60 tw-cursor-default" : ""}`}>
              {ft.label} <span className="tw-opacity-60">· {ft.iso}</span>
            </Clickable>
          ))}
          {/* Luồng ký khác nhau giữa 2 mẫu — hiện ngay để người lập biết phiếu sẽ đi qua ai. */}
          <span className="tw-w-full tw-text-[11px] tw-text-geist-fg-3 tw-mt-1">
            Approval flow: {(FORM_TYPES.find((x) => x.value === form.form_type) || FORM_TYPES[0]).flow}
          </span>
        </div>

        <StepperBar steps={STEPS} step={step} onJump={jumpTo} />

        {step === 0 && (
          <FormSection icon="bi-clipboard-data" title="Basic info">
            <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-x-6 tw-gap-y-4">
              <div>
                <FieldLabel required>Site</FieldLabel>
                <SiteSelect value={form.site_id} onChange={(v) => setField("site_id", v)} fetcher={getPurchaseRequestSiteOptions} />
              </div>
              <div>
                <FieldLabel required>Head of Dept (approver)</FieldLabel>
                <UserPicker value={form.head_of_dept} onChange={(u) => setField("head_of_dept", u)}
                  fetcher={searchPurchaseRequestUsers} placeholder="Select Head of Dept..." />
              </div>
              <GeistInput label="Department" required value={form.department} onChange={(e) => setField("department", e.target.value)} />
              <GeistInput label="Requester position" value={form.requester_position} onChange={(e) => setField("requester_position", e.target.value)} />
              {form.form_type === "raw_material" && (
                <GeistInput label="From (Warehouse)" value={form.from_dept} onChange={(e) => setField("from_dept", e.target.value)} />
              )}
              <GeistInput label="To" value={form.to_dept} onChange={(e) => setField("to_dept", e.target.value)} />
              <GeistInput label="Request date" type="date" value={form.request_date} onChange={(e) => setField("request_date", e.target.value)} />
            </div>
          </FormSection>
        )}

        {step === 1 && (
          <>
            <FormSection icon="bi-truck" title="Suggested vendor">
              <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-x-6 tw-gap-y-4">
                <GeistInput label="Name" value={form.vendor_name} onChange={(e) => setField("vendor_name", e.target.value)} />
                <GeistInput label="Address" value={form.vendor_address} onChange={(e) => setField("vendor_address", e.target.value)} />
                <GeistInput label="Attn." value={form.vendor_attn} onChange={(e) => setField("vendor_attn", e.target.value)} />
                <GeistInput label="Phone" value={form.vendor_phone} onChange={(e) => setField("vendor_phone", e.target.value)} />
                <GeistInput label="Fax" value={form.vendor_fax} onChange={(e) => setField("vendor_fax", e.target.value)} />
                <GeistInput label="Title" value={form.vendor_title} onChange={(e) => setField("vendor_title", e.target.value)} />
              </div>
            </FormSection>

            {nonRaw ? (
              <FormSection icon="bi-geo-alt" title="Delivery & reason">
                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-x-6 tw-gap-y-4">
                  <GeistInput label="Reason for purchase" type="textarea" rows={2} wrapperClassName="sm:tw-col-span-3"
                    value={form.reason_for_purchase} onChange={(e) => setField("reason_for_purchase", e.target.value)} />
                  <GeistInput label="Delivery to" value={form.delivery_to} onChange={(e) => setField("delivery_to", e.target.value)} />
                  <GeistInput label="Location" value={form.delivery_location} onChange={(e) => setField("delivery_location", e.target.value)} />
                  <GeistInput label="Attn." value={form.delivery_attn} onChange={(e) => setField("delivery_attn", e.target.value)} />
                </div>
              </FormSection>
            ) : (
              /* QF-LGS-01: 3 nhóm checkbox đúng như mẫu giấy — Reason for Purchase (multi),
                 Delivery to (chọn 1), Attached file (multi). "Other" mở text tự do. */
              <FormSection icon="bi-geo-alt" title="Delivery & reason">
                <div className="tw-space-y-4">
                  <div>
                    <FieldLabel>Reason for Purchase</FieldLabel>
                    <div className="tw-flex tw-flex-wrap tw-gap-2 tw-mt-1">
                      {REASON_KINDS.map((k) => (
                        <CheckPill key={k} label={k} checked={form.reason_kinds.includes(k)}
                          onClick={() => setField("reason_kinds", form.reason_kinds.includes(k)
                            ? form.reason_kinds.filter((x) => x !== k) : [...form.reason_kinds, k])} />
                      ))}
                    </div>
                    {form.reason_kinds.includes("Other") && (
                      <div className="tw-mt-2">
                        <GeistInput label="Other (specify)" value={form.reason_for_purchase}
                          onChange={(e) => setField("reason_for_purchase", e.target.value)} />
                      </div>
                    )}
                  </div>
                  <div>
                    <FieldLabel>Delivery to</FieldLabel>
                    <div className="tw-flex tw-flex-wrap tw-gap-2 tw-mt-1">
                      {DELIVERY_KINDS.map((k) => (
                        <CheckPill key={k} label={k} checked={form.delivery_kind === k}
                          onClick={() => setField("delivery_kind", form.delivery_kind === k ? "" : k)} />
                      ))}
                    </div>
                    <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-x-6 tw-gap-y-3 tw-mt-2">
                      {form.delivery_kind === "Other" && (
                        <GeistInput label="Other (specify)" value={form.delivery_to}
                          onChange={(e) => setField("delivery_to", e.target.value)} />
                      )}
                      <GeistInput label="Location" value={form.delivery_location} onChange={(e) => setField("delivery_location", e.target.value)} />
                      <GeistInput label="Attn." value={form.delivery_attn} onChange={(e) => setField("delivery_attn", e.target.value)} />
                    </div>
                  </div>
                  <div>
                    <FieldLabel>Attached file</FieldLabel>
                    <div className="tw-flex tw-flex-wrap tw-gap-2 tw-mt-1">
                      {ATTACHMENT_KINDS.map((k) => (
                        <CheckPill key={k} label={k} checked={form.attachment_kinds.includes(k)}
                          onClick={() => setField("attachment_kinds", form.attachment_kinds.includes(k)
                            ? form.attachment_kinds.filter((x) => x !== k) : [...form.attachment_kinds, k])} />
                      ))}
                    </div>
                  </div>
                </div>
              </FormSection>
            )}
          </>
        )}

        {step === 2 && (
          <>
            <FormSection icon="bi-list-ul" title="Item lines" bodyClassName="tw-p-3 sm:tw-p-4"
              right={
                <Clickable onClick={addItem}
                  className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-semibold tw-text-geist-accent hover:tw-underline">
                  <FaPlus size={9} /> Add line
                </Clickable>
              }>
              <div className="tw-overflow-x-auto">
                <table className="tw-w-full tw-border-collapse">
                  <thead>
                    <tr className="tw-border-0 tw-border-b tw-border-solid tw-border-geist-border">
                      <th className={`${TH} tw-min-w-[90px]`}>Code No</th>
                      <th className={`${TH} tw-min-w-[180px]`}>Description</th>
                      <th className={`${TH} tw-min-w-[130px]`}>Required date</th>
                      <th className={`${TH} tw-min-w-[70px]`}>Unit</th>
                      <th className={`${TH} tw-text-right`}>Qty</th>
                      {nonRaw && <th className={`${TH} tw-text-right`}>Unit price</th>}
                      {nonRaw && <th className={`${TH} tw-text-right`}>Amount</th>}
                      <th className={`${TH} tw-min-w-[120px]`}>Remark</th>
                      <th className={`${TH} tw-w-8`} />
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.map((it, idx) => (
                      <tr key={it._key} className={idx % 2 === 1 ? "tw-bg-geist-bg-2" : "tw-bg-white"}>
                        <td className="tw-px-2 tw-py-1.5"><input className={cellInput} value={it.code_no || ""} onChange={(e) => setItem(it._key, { code_no: e.target.value })} /></td>
                        <td className="tw-px-2 tw-py-1.5"><input className={cellInput} value={it.description || ""} onChange={(e) => setItem(it._key, { description: e.target.value })} /></td>
                        <td className="tw-px-2 tw-py-1.5"><input type="date" className={cellInput} value={it.required_date || ""} onChange={(e) => setItem(it._key, { required_date: e.target.value })} /></td>
                        <td className="tw-px-2 tw-py-1.5"><input className={cellInput} value={it.unit || ""} onChange={(e) => setItem(it._key, { unit: e.target.value })} /></td>
                        <td className="tw-px-2 tw-py-1.5"><input type="number" className={`${cellInput} tw-text-right`} value={it.qty || ""} onChange={(e) => setItem(it._key, { qty: e.target.value })} /></td>
                        {nonRaw && <td className="tw-px-2 tw-py-1.5"><input type="number" className={`${cellInput} tw-text-right`} value={it.unit_price || ""} onChange={(e) => setItem(it._key, { unit_price: e.target.value })} /></td>}
                        {nonRaw && <td className="tw-px-2 tw-py-1.5 tw-text-right tw-text-[13px] tw-tabular-nums tw-text-geist-fg-2">{(Number(it.amount) || 0).toLocaleString("en-US")}</td>}
                        <td className="tw-px-2 tw-py-1.5"><input className={cellInput} value={it.remark || ""} onChange={(e) => setItem(it._key, { remark: e.target.value })} /></td>
                        <td className="tw-px-2 tw-py-1.5 tw-text-center">
                          <Clickable onClick={() => removeItem(it._key)} className="tw-inline-flex tw-text-geist-fg-3 hover:tw-text-geist-error"><FaTrash size={11} /></Clickable>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="tw-border-t-2 tw-border-solid tw-border-geist-border tw-bg-geist-bg-2">
                      <td className="tw-px-2 tw-py-2.5 tw-text-right tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-geist-fg-2" colSpan={4}>Total</td>
                      <td className="tw-px-2 tw-py-2.5 tw-text-right tw-text-sm tw-font-bold tw-text-geist-fg tw-tabular-nums">{totalWeight.toLocaleString("en-US")}</td>
                      {nonRaw && <td className="tw-px-2 tw-py-2.5" />}
                      {nonRaw && <td className="tw-px-2 tw-py-2.5 tw-text-right tw-text-sm tw-font-bold tw-text-geist-fg tw-tabular-nums">{totalAmount.toLocaleString("en-US")}</td>}
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </FormSection>

            <FormSection icon="bi-chat-left-text" title="Notes">
              {/* Label theo đúng mẫu in: QF-LGS-01 là ô "Additional information to Purchasing
                  (if any)" trong khối chữ ký; QF-PUR-01 là "Notes (Additional information to Buyer)". */}
              <GeistInput
                label={nonRaw ? "Additional information to Buyer" : "Additional information to Purchasing (if any)"}
                type="textarea" rows={2}
                value={form.notes} onChange={(e) => setField("notes", e.target.value)} />
            </FormSection>
          </>
        )}
      </div>
    </SlideDrawer>
  );
};

const idType = PropTypes.oneOfType([PropTypes.number, PropTypes.string]);
const numeric = PropTypes.oneOfType([PropTypes.number, PropTypes.string]);

PurchaseRequestDrawer.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
  // Present when editing; absent for a new requisition.
  purchaseRequest: PropTypes.shape({
    id: idType,
    code: PropTypes.string,
    form_type: PropTypes.oneOf(FORM_TYPES.map((t) => t.value)),
    department: PropTypes.string,
    requester_position: PropTypes.string,
    from_dept: PropTypes.string,
    to_dept: PropTypes.string,
    request_date: PropTypes.string,
    site_id: idType,
    vendor_name: PropTypes.string,
    vendor_address: PropTypes.string,
    vendor_phone: PropTypes.string,
    vendor_fax: PropTypes.string,
    vendor_attn: PropTypes.string,
    vendor_title: PropTypes.string,
    reason_for_purchase: PropTypes.string,
    reason_kinds: PropTypes.arrayOf(PropTypes.string),
    delivery_kind: PropTypes.string,
    attachment_kinds: PropTypes.arrayOf(PropTypes.string),
    delivery_to: PropTypes.string,
    delivery_location: PropTypes.string,
    delivery_attn: PropTypes.string,
    head_of_dept_user_id: idType,
    head_of_dept_email: PropTypes.string,
    head_of_dept_name: PropTypes.string,
    notes: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.shape({
      id: idType,
      code_no: PropTypes.string,
      description: PropTypes.string,
      required_date: PropTypes.string,
      unit: PropTypes.string,
      qty: numeric,
      unit_price: numeric,
      amount: numeric,
      remark: PropTypes.string,
    })),
  }),
  initialFormType: PropTypes.oneOf(FORM_TYPES.map((t) => t.value)),
  onSaved: PropTypes.func,
};

export default PurchaseRequestDrawer;
